import type { ComponentChildren, ContextType, Ref, JSX } from 'preact';

import type { UnpackSignals } from '@oracle/oraclejet-internal-utilities/attributeUtils';

import { useAccessibleContext } from '#hooks/UNSAFE_useAccessibleContext';
import { useComponentTheme } from '#hooks/UNSAFE_useComponentTheme';
import { useFormContext } from '#hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import type { FormVariantContext } from '#hooks/UNSAFE_useFormVariantContext';
import { useTabbableMode } from '#hooks/UNSAFE_useTabbableMode';
import { useTranslationBundle } from '#hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '#resources/nls/bundle';
import { classNames } from '#utils/UNSAFE_classNames';
import { textInterpolations, type TextProps } from '#utils/UNSAFE_interpolations/text';
import { mergeInterpolations } from '#utils/UNSAFE_mergeInterpolations';
import { formControlInheritTextAlign } from './themes/FormControlUtilsStyles.css';
import {
  ReadonlyTextFieldInputStyles,
  type ReadonlyTextFieldInputVariantOptions
} from './themes/ReadonlyTextFieldInputStyles.css';
import { ReadonlyTextFieldInputRedwoodTheme } from './themes/redwood/ReadonlyTextFieldInputTheme';

type AutoFocusProp = UnpackSignals<JSX.IntrinsicElements['input']>['autofocus'];

type FocusProps = Pick<UnpackSignals<JSX.HTMLAttributes<HTMLElement>>, 'onBlur' | 'onFocus'>;

type ReadonlyAsInputProps = {
  as: 'input';
  type: UnpackSignals<JSX.IntrinsicElements['input']>['type'];
  elementRef?: Ref<HTMLInputElement>;
  rows?: never;
};

type ReadonlyAsTextAreaProps = {
  as: 'textarea';
  elementRef?: Ref<HTMLTextAreaElement | HTMLDivElement>;
  type?: never;
  rows?: number;
};

type ReadonlyAsDivProps = {
  as?: 'div';
  elementRef?: Ref<HTMLTextAreaElement | HTMLDivElement>;
  type?: never;
  rows?: never;
};

type Props = TextProps &
  (ReadonlyAsDivProps | ReadonlyAsInputProps | ReadonlyAsTextAreaProps) &
  FocusProps & {
    'aria-describedby'?: string;
    'aria-label'?: string;
    'aria-labelledby'?: string;
    autoFocus?: AutoFocusProp;
    formVariant?: ContextType<typeof FormVariantContext>;
    hasEmptyLabel?: boolean;
    hasInsideLabel?: boolean;
    id?: UnpackSignals<JSX.HTMLAttributes<HTMLElement>>['id'];
    inlineUserAssistance?: ComponentChildren;
    innerReadonlyField?: ComponentChildren;
    value?: string;
    variant?: 'textarea';
  };

/**
 * Merge multiple ids into a space-separated list
 * @param ids The ids to be merged
 * @returns The space separated string of ids
 */
function mergeIDs(...ids: (string | undefined)[]) {
  return ids.filter(Boolean).join(' ') || undefined;
}

export function ReadonlyTextFieldInput({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': inputLabelledBy,
  autoFocus,
  as = 'div',
  elementRef,
  formVariant,
  hasEmptyLabel,
  hasInsideLabel = false,
  inlineUserAssistance,
  innerReadonlyField,
  onBlur,
  onFocus,
  rows,
  type,
  value = '',
  variant,
  ...props
}: Props) {
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const loadingAriaLabel = translations.formControl_loading();
  const { isFormLayout, isReadonly: isReadonlyForm } = useFormContext();
  const { isLoading } = useFormFieldContext();
  const ariaLabelForReadonly = isLoading ? loadingAriaLabel : ariaLabel;
  const focusProps = { onBlur, onFocus };

  // get classes for text align
  const interpolations = [...Object.values(textInterpolations)];
  const styleInterpolations = mergeInterpolations<Props>(interpolations);
  const { class: styleInterpolationClasses } = styleInterpolations(props);
  const { classes: themeClasses } = useComponentTheme<
    ReadonlyTextFieldInputVariantOptions,
    ReadonlyTextFieldInputStyles
  >(ReadonlyTextFieldInputRedwoodTheme, {
    textarea: variant === 'textarea' ? 'isTextArea' : 'notTextArea',
    formLayout: isFormLayout ? 'isFormLayout' : 'notFormLayout',
    readonlyForm: isReadonlyForm ? 'isReadonlyForm' : 'notReadonlyForm',
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel'
  });

  const readonlyDivClasses = classNames([
    themeClasses,
    styleInterpolationClasses,
    // JET-72775 - Form control alignment in tables
    // When we are in legacy collection components, we want to inherit the text align
    // when it is not set explicitly.
    formVariant === 'legacyEmbedded' && props.textAlign === undefined && formControlInheritTextAlign
  ]);

  // JET-52914 - Implement Tabbable mode API contract in InputText
  // spread tabbableModeProps on component
  const { isTabbable, tabbableModeProps } = useTabbableMode();

  // JET-52089: add unsafe API for specifying an external label which is added to ariaLabelledBy on the input or div,
  // only when labelEdge is 'none' and label is ''. Since ariaLabelledBy takes precedence over all other
  // kinds of labels, this helps to ensure we don't override a meaningful label.
  const { UNSAFE_ariaLabelledBy } = useAccessibleContext();
  const ariaLabelledBy = hasEmptyLabel
    ? mergeIDs(inputLabelledBy, UNSAFE_ariaLabelledBy)
    : inputLabelledBy;

  if (as === 'input') {
    return (
      <input
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabelForReadonly}
        aria-labelledby={ariaLabelledBy}
        autofocus={autoFocus}
        class={readonlyDivClasses}
        readonly={true}
        // @ts-expect-error TS cannot infer the type correctly for the polymorphed ref
        ref={elementRef}
        type={type}
        value={value}
        {...(!isTabbable && tabbableModeProps)}
        {...(hasInsideLabel && focusProps)}
      />
    );
  }
  if (as === 'textarea') {
    return (
      <textarea
        aria-describedby={ariaDescribedBy}
        aria-label={ariaLabelForReadonly}
        aria-labelledby={ariaLabelledBy}
        autofocus={autoFocus}
        class={readonlyDivClasses}
        readonly={true}
        // @ts-expect-error TS cannot infer the type correctly for the polymorphed ref
        ref={elementRef}
        rows={rows}
        {...(!isTabbable && tabbableModeProps)}
        {...(hasInsideLabel && focusProps)}>
        {value}
      </textarea>
    );
  }
  return (
    <div
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabelForReadonly}
      aria-labelledby={ariaLabelledBy}
      aria-readonly={true}
      autofocus={autoFocus}
      class={readonlyDivClasses}
      // @ts-expect-error TS cannot infer the type correctly for the polymorphed ref
      ref={elementRef}
      role="textbox"
      {...tabbableModeProps}
      {...(hasInsideLabel && focusProps)}>
      {value}
    </div>
  );
}
